import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaTv,
  FaFilter,
  FaTrash,
  FaTools,
  FaCheckCircle,
  FaClock,
  FaExclamationTriangle,
} from "react-icons/fa";
import Api from "../../api";
import AddPerawatan from "../../components/aset/AddPerawatan";
import DataPerawatan from "../../components/aset/DataPerawatan";
import StatPerawatan from "../../components/aset/StatPerawatan";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import { prepareImageForUpload } from "../../utils/imageCompress";
import { SipDropdown, SipSearch } from "../../components/form/SipControls";

/**
 * Komponen PerawatanAset - Halaman untuk mengelola data perawatan aset
 * Fitur: CRUD perawatan, filter, pencarian, pagination, dan statistik
 */
function PerawatanAset() {
  const navigate = useNavigate();

  // ==================== STATE MANAGEMENT ====================

  // State untuk pencarian dan filter
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [merkFilter, setMerkFilter] = useState("");
  const [jenisKerusakanFilter, setJenisKerusakanFilter] = useState("");



  // State untuk data dan loading
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // State untuk modal dan form
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState({
    id: "",
    jenis_kerusakan: "",
    deskripsi: "",
    tindakan: "",
    foto: null,
    tgl_masuk: "",
    tgl_selesai: "",
    inventaris_id: "",
    petugas: "",
    ket: "",
    status: "",
    desc: "",
    aset: "",
  });

  const resetData = () => {
    setForm({
      id: "",
      jenis_kerusakan: "",
      deskripsi: "",
      tindakan: "",
      foto: null,
      tgl_masuk: "",
      tgl_selesai: "",
      inventaris_id: "",
      petugas: "",
      ket: "",
      status: "",
      desc: "",
      aset: "",
    });
  };

  // ==================== API FUNCTIONS ====================

  /**
   * Fungsi untuk mengirim data perawatan baru ke server
   * @param {FormData} data - Data form yang akan dikirim
   * @returns {Promise} Response dari server
   */
  const postPerawatan = async () => {
    try {
      const formData = new FormData();
      formData.append("id", form.id);
      formData.append("jenis_kerusakan", form.jenis_kerusakan);
      formData.append("deskripsi", form.deskripsi);
      formData.append("tindakan", form.tindakan);
      formData.append("tgl_masuk", form.tgl_masuk);
      formData.append("tgl_selesai", form.tgl_selesai);
      formData.append("inventaris_id", form.inventaris_id);
      formData.append("petugas", form.petugas);
      formData.append("ket", form.ket);
      formData.append("status", form.status);
      formData.append("desc", form.desc);
      formData.append("aset", form.aset);

      // Tambahkan foto hanya jika file baru — pastikan sudah dikompres
      if (form.foto instanceof File) {
        const foto = await prepareImageForUpload(form.foto);
        if (foto) formData.append("foto", foto);
      }

      const res = await Api.post("/mon/perawatan-aset", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      resetData();
      toast.success("Laporan kerusakan berhasil dikirim!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      setIsModalOpen(false);
      getDataPerawatan(null, null, false);
      return res;
    } catch (error) {
      console.error("Error mengirim laporan:", error);
      // Tampilkan pesan error dengan toast
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        toast.error("Gagal mengirim laporan: " + error.response.data.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else {
        toast.error("Gagal mengirim laporan. Silakan coba lagi.", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    }
  };

  /**
   * Fungsi untuk mengambil data perawatan dari server
   * @param {string|null} spec - Filter berdasarkan jenis kerusakan
   * @param {string|null} merk - Filter berdasarkan merk
   * @param {boolean} isInitialLoad - Apakah ini loading pertama kali
   */
  const getDataPerawatan = async (spec = null, merk = null, isInitialLoad = false) => {
    try {
      setLoading(true);
      // Membangun query parameters
      const params = new URLSearchParams();
      if (spec) params.append("spec", spec);
      if (merk) params.append("merk", merk);

      const queryString = params.toString();
      const url = `/mon/perawatan-aset${queryString ? `?${queryString}` : ""}`;

      const res = await Api.get(url);
      setRows(res.data.data || []);
    } catch (error) {
      console.error("Error fetching perawatan data:", error);
      setError("Gagal mengambil data perawatan. Silakan coba lagi.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (id) => {
    try {
      const res = await Api.get(`/mon/perawatan-aset/${id}`);
      const data = res.data.data || {};
      setForm({
        id: data.id || "",
        jenis_kerusakan: data.jenis_kerusakan || "",
        deskripsi: data.desc || "",
        tindakan: data.tindakan || "",
        foto: data.foto || null,
        tgl_masuk: data.tgl_masuk ? dayjs(data.tgl_masuk).format("YYYY-MM-DD") : "",
        tgl_selesai: data.tgl_selesai ? dayjs(data.tgl_selesai).format("YYYY-MM-DD") : "",
        inventaris_id: data.inventaris_id || "",
        petugas: data.pic || "",
        ket: data.ket || "",
        status: data.status || "",
        desc: data.desc || "",
        aset: "",
      });
      setIsModalOpen(true);
    } catch (error) {
      console.error("Error fetching edit data:", error);
      toast.error("Gagal memuat data untuk edit. Silakan coba lagi.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } 
  }
  const handleDelete = async (id) => {
    try {
      // Add id to deleting set
      
      await Api.delete(`/mon/perawatan-aset/${id}`);
      toast.success("Data berhasil dihapus!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      getDataPerawatan();
    } catch (error) {
      console.error("Error deleting data:", error);
      toast.error("Gagal menghapus data. Silakan coba lagi.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } 
  }

  // ==================== EVENT HANDLERS ====================

  /**
   * Handler untuk navigasi kembali
   */
  const handleBack = () => {
    navigate(-1);
  };

  /**
   * Handler untuk perubahan filter dengan debounce
   */
  const handleFilterChange = async () => {
    const spec = jenisKerusakanFilter || null;
    const merk = merkFilter || null;

    if (spec || merk) {
      
    }

    try {
      await getDataPerawatan(spec, merk, false);
    } finally {
      
    }
  };

  // ==================== UTILITY FUNCTIONS ====================

  /**
   * Fungsi untuk mendapatkan badge status dengan styling yang sesuai
   * @param {string} status - Status perawatan
   * @returns {JSX.Element} Badge component
   */
  const getStatusBadge = (status) => {
    const statusConfig = {
      selesai: {
        bg: "bg-green-100",
        text: "text-green-800",
        icon: <FaCheckCircle className="w-3 h-3" />,
        label: "Selesai",
      },
      proses: {
        bg: "bg-blue-100",
        text: "text-blue-800",
        icon: <FaClock className="w-3 h-3" />,
        label: "Dalam Proses",
      },
      dijadwalkan: {
        bg: "bg-yellow-100",
        text: "text-yellow-800",
        icon: <FaExclamationTriangle className="w-3 h-3" />,
        label: "Dijadwalkan",
      },
    };

    const config = statusConfig[status] || statusConfig.dijadwalkan;

    return (
      <span
        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold leading-none ${config.bg} ${config.text}`}
      >
        <span className="inline-flex shrink-0">{config.icon}</span>
        {config.label}
      </span>
    );
  };

  // ==================== EFFECTS ====================

  /**
   * Effect untuk load data awal saat komponen mount
   */
  useEffect(() => {
    getDataPerawatan(null, null, true);
  }, []);

  /**
   * Effect untuk menerapkan filter dengan debounce (500ms)
   * Mencegah terlalu banyak request saat user mengetik
   */
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleFilterChange();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [jenisKerusakanFilter, merkFilter]);

  // ==================== DATA PROCESSING ====================

  /**
   * Filter data berdasarkan pencarian dan status
   */
  const filteredData = rows.filter((item) => {
    const matchesSearch =
      (item.inventaris_desc || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (item.kode || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.pic || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.merk || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.jenis_kerusakan || "")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });



  // ==================== RENDER CONDITIONS ====================

  /**
   * Loading State - Tampilkan spinner saat data sedang dimuat
   */
  

  /**
   * Error State - Tampilkan pesan error jika terjadi kesalahan
   */
  if (error) {
    return (
      <div className="space-y-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="px-6 py-12 text-center">
              <FaExclamationTriangle className="mx-auto h-12 w-12 text-red-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Terjadi Kesalahan
              </h3>
              <p className="text-sm text-gray-500 mb-6">{error}</p>
              <button
                onClick={() => getDataPerawatan(null, null, true)}
                className="inline-flex items-center gap-2 bg-primary text-white px-6 py-3 rounded-xl hover:bg-primary/90 transition-all duration-200 shadow-md hover:shadow-lg font-medium"
              >
                Coba Lagi
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==================== MAIN RENDER ====================

  return (
    <div className="space-y-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* ==================== HEADER SECTION ==================== */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-primary px-6 py-8 md:px-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Judul Halaman */}
              <div className="text-white animate__animated animate__fadeInLeft animate__delay-2s">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-white/20 rounded-lg animate__animated animate__pulse animate__infinite animate__slower">
                    <FaTv />
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold">
                    Perawatan Aset
                  </h1>
                </div>
                <p className="text-primary text-sm md:text-base">
                  Pantau dan kelola perawatan dan perbaikan aset
                </p>
              </div>

              {/* Tombol Kembali */}
              <button
                onClick={handleBack}
                className="flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white border border-white/20 px-4 py-3 rounded-xl hover:bg-white/20 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                <span className="font-medium">Kembali</span>
              </button>
            </div>
          </div>
        </div>

        {/* ==================== FILTER & SEARCH SECTION ==================== */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <FaFilter />
              Filter & Pencarian
            </h2>
          </div>

          <div className="p-6">
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {/* Input Pencarian */}
              <div className="min-w-0 sm:col-span-2">
                <SipSearch
                  placeholder="Cari berdasarkan nama aset, kode, merk, teknisi, atau jenis kerusakan..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="min-w-0">
                <SipDropdown
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  options={[
                    { label: "Semua Status", value: "all" },
                    { label: "Selesai", value: "selesai" },
                    { label: "Dalam Proses", value: "proses" },
                    { label: "Dijadwalkan", value: "terjadwal" },
                  ]}
                />
              </div>

              {/* Tombol Tambah Perawatan */}
              <AddPerawatan
                form={form}
                setForm={setForm}
                postData={postPerawatan}
                onSuccess={() => getDataPerawatan()}
                setIsModalOpen={setIsModalOpen}
                isModalOpen={isModalOpen}
                reset={resetData}
              />
            </div>

            {/* Status Filter dan Tombol Clear */}
            <div className="mt-4 flex items-center justify-between">
              
              {/* Tombol Hapus Filter */}
              {(merkFilter || jenisKerusakanFilter) && (
                <button
                  onClick={() => {
                    setMerkFilter("");
                    setJenisKerusakanFilter("");
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 transition-all duration-200 ml-auto"
                >
                  <FaTrash className="w-3 h-3" />
                  Hapus Filter
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ==================== STATISTICS CARDS ==================== */}
        <StatPerawatan rows={rows} />

        {/* ==================== TABLE SECTION ==================== */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Header Tabel */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  Data Perawatan Aset
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Total {filteredData.length} data perawatan
                </p>
              </div>
            </div>
          </div>

          {/* Tabel Data */}
          <div className="p-6">
            <DataPerawatan
              currentData={filteredData}
              getStatusBadge={getStatusBadge}
              // handleView={handleView}
              handleDelete={handleDelete}
              handleEdit={handleEdit}
              loading={loading}
            />
          </div>
        </div>

        {/* ==================== EMPTY STATE ==================== */}
        {filteredData.length === 0 && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden animate__animated animate__fadeInUp">
            <div className="px-6 py-12 text-center">
              <FaTools className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Tidak ada data perawatan
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                {searchTerm || statusFilter !== "all"
                  ? "Tidak ada data yang sesuai dengan filter yang dipilih."
                  : "Belum ada data perawatan aset yang tersedia."}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default PerawatanAset;